// mui
import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';

// helpers
import { formatDate } from '@karehero/helpers/datetime';

export interface ShortlistCardData {
  id: string;
  name: string;
  providerCount: number;
  securedCount: number;
  updatedAt: string;
  onClick: () => void;
}

export interface CareSearchShortlistsPageProps {
  careRecipientFirstName: string;
  shortlists: ShortlistCardData[];
  isLoading?: boolean;
}

export const CareSearchShortlistsPage = ({
  careRecipientFirstName,
  shortlists,
  isLoading,
}: CareSearchShortlistsPageProps) => {
  const theme = useTheme();

  return (
    <PageTemplate
      headerSlot={
        <PageHeader
          title='Care search'
          description={`Shortlists your care expert has created for ${careRecipientFirstName}'s care.`}
        />
      }
      contentSlot={
        <Stack spacing={2}>
          {isLoading && (
            <Stack
              sx={{
                alignItems: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Stack>
          )}
          {!isLoading &&
            shortlists.map((shortlist) => (
              <Card key={shortlist.id}>
                <CardActionArea onClick={shortlist.onClick}>
                  <CardContent>
                    <Stack
                      direction='row'
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant='h5'>{shortlist.name}</Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {shortlist.providerCount}{' '}
                          {shortlist.providerCount === 1
                            ? 'provider'
                            : 'providers'}{' '}
                          &middot; Updated{' '}
                          {formatDate(shortlist.updatedAt, { omitDay: true })}
                        </Typography>
                        {shortlist.securedCount > 0 && (
                          <Stack
                            direction='row'
                            sx={{
                              mt: 0.5,
                            }}
                          >
                            <Chip
                              label={`${shortlist.securedCount} secured`}
                              size='small'
                              sx={{
                                backgroundColor:
                                  theme.palette.custom.limeGreen[500],
                                color: theme.palette.common.white,
                                fontWeight: 600,
                              }}
                            />
                          </Stack>
                        )}
                      </Stack>
                      <Icon icon='chevron-right' />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
        </Stack>
      }
    />
  );
};

CareSearchShortlistsPage.displayName = 'CareSearchShortlistsPage';

export default CareSearchShortlistsPage;
